import { MapPin, Phone, Mail, Clock, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { DevisForm } from "@/components/forms/DevisForm";
import { ContactForm } from "@/components/forms/ContactForm";
import { site } from "@/config/site";

const { lat, lng } = site.address.geo;
const bbox = `${lng - 0.012},${lat - 0.006},${lng + 0.012},${lat + 0.006}`;
const OSM_EMBED = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
const OSM_LINK = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact et demande de devis | MINES DE PARIS"
        description="Contactez MINES DE PARIS à Pantin (93) : formulaire de demande de devis B2B, formulaire de contact, téléphone +33 7 46 52 86 72, email et horaires. Réponse sous 48 heures ouvrées."
        path="/contact"
        breadcrumb="Contact"
      />
      <PageHero
        crumb="Contact"
        eyebrow="Contact & devis"
        lines={["Décrivez votre besoin,", "nous revenons vers vous", "sous 48 h ouvrées"]}
        intro="Demande de devis, recherche de fournisseur ou simple question : utilisez le formulaire adapté. Les informations transmises sont confidentielles et ne sont en aucun cas cédées à des tiers."
      />

      <section className="py-16 sm:py-24" data-testid="contact-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <aside className="space-y-8 lg:col-span-4" data-testid="contact-coordinates">
            <Reveal className="card p-7">
              <h2 className="font-heading text-xl font-bold text-night">Coordonnées</h2>
              <ul className="mt-6 space-y-5 text-sm">
                <li className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" /><address className="not-italic text-ink">{site.address.street}<br />{site.address.postalCode} {site.address.city}<br />{site.address.country}</address></li>
                <li className="flex gap-3"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" /><a href={site.phoneHref} className="text-ink hover:text-gold" data-testid="contact-phone-link">{site.phone}</a></li>
                <li className="flex gap-3"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" /><a href={site.emailHref} className="text-ink hover:text-gold" data-testid="contact-email-link">{site.email}</a></li>
                <li className="flex gap-3"><Clock className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" /><span className="text-ink">{site.hours}</span></li>
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="card overflow-hidden">
              {/* Carte OpenStreetMap : iframe sans cookie tiers. */}
              <iframe
                title={`Carte — ${site.address.full}`}
                src={OSM_EMBED}
                width="100%"
                height="300"
                loading="lazy"
                className="block w-full"
                referrerPolicy="no-referrer"
                data-testid="contact-map"
              />
              <p className="px-5 py-3 text-xs text-muted">
                <a href={OSM_LINK} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-gold">Afficher une carte plus grande</a> — © contributeurs OpenStreetMap
              </p>
            </Reveal>
            <Reveal delay={0.15} className="flex gap-3 rounded-xl border border-gold/40 bg-gold-pale/60 p-5 text-sm text-ink">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" />
              <p>Les informations transmises sont confidentielles et ne sont en aucun cas cédées à des tiers.</p>
            </Reveal>
          </aside>

          <Reveal delay={0.1} className="lg:col-span-8">
            <div id="devis" className="card scroll-mt-28 p-6 sm:p-10">
              <Tabs defaultValue="devis">
                <TabsList className="mb-8 grid h-auto w-full grid-cols-2 rounded-full bg-mist p-1">
                  <TabsTrigger value="devis" className="rounded-full py-2.5 text-sm font-semibold data-[state=active]:bg-night data-[state=active]:text-white" data-testid="tab-devis">Demande de devis</TabsTrigger>
                  <TabsTrigger value="contact" className="rounded-full py-2.5 text-sm font-semibold data-[state=active]:bg-night data-[state=active]:text-white" data-testid="tab-contact">Contact simple</TabsTrigger>
                </TabsList>
                <TabsContent value="devis">
                  <h2 className="font-heading text-2xl font-bold text-night">Demande de devis</h2>
                  <p className="mt-2 text-sm text-muted">Plus votre description est précise, plus notre proposition sera rapide et adaptée.</p>
                  <div className="relative mt-8"><DevisForm /></div>
                </TabsContent>
                <TabsContent value="contact">
                  <h2 className="font-heading text-2xl font-bold text-night">Nous écrire</h2>
                  <p className="mt-2 text-sm text-muted">Une question, une suggestion ou une demande de partenariat ? Écrivez-nous.</p>
                  <div className="relative mt-8"><ContactForm /></div>
                </TabsContent>
              </Tabs>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
